import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const DATE_PICKER_CSS = `
  .nav-btn {
    background: transparent !important;
    border: none !important;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #475569;
    transition: color 0.2s;
  }
  .nav-btn:hover {
    color: #4F46E5;
  }
  .nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .header-month-select {
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    padding: 4px 10px;
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    cursor: pointer;
    background: white;
  }

  .header-year-input {
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    padding: 4px 8px;
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    width: 75px;
    text-align: center;
    background: white;
  }

  .header-year-input:focus, .header-month-select:focus {
    border-color: #4F46E5;
  }

  /* Main Card Overrides - Clean Classic */
  .react-datepicker {
    background-color: #ffffff !important;
    border: 1px solid #cbd5e0 !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
    padding: 5px !important;
    font-family: inherit !important;
  }

  .react-datepicker__header {
    background-color: transparent !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 8px 10px 10px 10px !important;
  }

  .react-datepicker__day-name {
    color: #64748b !important;
    font-weight: 600 !important;
    width: 2.22rem !important;
    margin: 0.1rem !important;
    font-size: 12px !important;
  }

  /* Day Styling - Compact Tiles */
  .react-datepicker__day {
    width: 2.22rem !important;
    height: 2.22rem !important;
    line-height: 2.22rem !important;
    margin: 0.1rem !important;
    border-radius: 6px !important;
    background: #F8F9FF !important;
    font-weight: 600 !important;
    color: #1e293b !important;
    font-size: 13px !important;
    border: 1px solid transparent !important;
    transition: background 0.2s;
  }

  .react-datepicker__day:hover {
    background: #E6E4FF !important;
    color: #4F46E5 !important;
  }

  .react-datepicker__day--selected {
    background-color: #4F46E5 !important;
    color: white !important;
  }

  .react-datepicker__day--outside-month {
    color: #94a3b8 !important;
    background: transparent !important;
  }

  .react-datepicker__month {
    margin: 0.2rem !important;
  }
`;

if (typeof document !== "undefined" && !document.getElementById("common-datepicker-styles")) {
  const styleEl = document.createElement("style");
  styleEl.id = "common-datepicker-styles";
  styleEl.textContent = DATE_PICKER_CSS;
  document.head.appendChild(styleEl);
}

const CommonDatePicker = ({
  selected,
  onChange,
  placeholderText = "Select date",
  dateFormat = "yyyy-MM-dd",
  disabled = false,
  className = "",
  portalId = "root-portal",
  error = "",
}) => {
  // Use state to track local year typing so user can type digit-by-digit
  const [yearValue, setYearValue] = React.useState("");

  // Sync year whenever selected date changes from outside
  React.useEffect(() => {
    if (selected) {
      setYearValue(selected.getFullYear().toString());
    } else {
      setYearValue(new Date().getFullYear().toString());
    }
  }, [selected]);

  const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      className={`form-control d-flex justify-content-between align-items-center ${className} ${
        disabled ? "bg-light cur-not-allowed" : "bg-white"
      }`}
      onClick={onClick}
      ref={ref}
      disabled={disabled}
      style={{
        borderRadius: "8px",
        minHeight: "38px",
        border: error ? "1px solid #FF4444" : "1px solid #dee2e6",
        color: value ? "#212129" : "#8790A1",
        fontSize: "14px",
        fontWeight: "400",
        textAlign: "left",
        width: "100%",
        boxShadow: "none",
        transition: "border-color 0.2s ease-in-out",
      }}
    >
      <span>{value || placeholderText}</span>
      <Calendar size={16} color="#8790A1" />
    </button>
  ));

  return (
    <div className="w-100 position-relative common-date-picker">
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat}
        placeholderText={placeholderText}
        disabled={disabled}
        portalId={portalId}
        showPopperArrow={false}
        fixedHeight
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ];
          
          return (
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="nav-btn"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
              >
                <ChevronLeft size={18} />
              </button>

              <div className="d-flex gap-2 align-items-center">
                <select
                  className="header-month-select"
                  value={months[date.getMonth()]}
                  onChange={({ target: { value } }) =>
                    changeMonth(months.indexOf(value))
                  }
                >
                  {months.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  className="header-year-input"
                  value={yearValue}
                  onChange={({ target: { value } }) => {
                    const val = value.slice(0, 4);
                    setYearValue(val);
                    if (val.length === 4) {
                      const year = parseInt(val);
                      if (!isNaN(year)) {
                        changeYear(year);
                      }
                    }
                  }}
                  onKeyDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <button
                type="button"
                className="nav-btn"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          );
        }}
        customInput={<CustomInput />}
      />
    </div>
  );
};

export default CommonDatePicker;
